import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  CardActions, 
  CircularProgress, 
  Alert,
  Collapse,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Download as DownloadIcon, 
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useLLMSOperations } from '../hooks/useLLMSOperations';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-1px)',
    transition: 'all 0.2s ease-in-out',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
  textTransform: 'none',
  borderRadius: '8px',
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  background: '#2563eb',
  color: '#ffffff',
  border: '1px solid #2563eb',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: '#1d4ed8',
    borderColor: '#1d4ed8',
    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
    transform: 'translateY(-1px)',
  },
  '&.Mui-disabled': {
    background: '#f7fafc',
    color: '#a0aec0',
    border: '1px solid #e2e8f0',
    boxShadow: 'none',
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
  textTransform: 'none',
  borderRadius: '8px',
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  background: 'transparent',
  color: '#2563eb',
  border: '1px solid #2563eb',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: '#ebf8ff',
    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
    transform: 'translateY(-1px)',
  },
}));

const PreviewContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: '#f7fafc',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  maxHeight: '400px',
  overflow: 'auto',
}));

const PreviewContent = styled('pre')(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  margin: 0,
  color: '#2d3748',
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Source Serif Pro', serif",
  fontSize: '1.4rem',
  fontWeight: 600,
  color: '#1a202c',
  marginBottom: theme.spacing(1),
}));

const CardSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '0.9rem',
  color: '#4a5568',
  marginBottom: theme.spacing(2),
  lineHeight: 1.5,
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    color: '#2d3748',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    '&:hover': {
      borderColor: '#cbd5e0',
    },
    '&.Mui-focused': {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
  },
  '& .MuiSelect-select': {
    color: '#2d3748',
    fontFamily: "'Inter', sans-serif",
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiSelect-icon': {
    color: '#4a5568',
  },
}));

const StyledInputLabel = styled(InputLabel)(({ theme }) => ({
  color: '#718096',
  fontFamily: "'Inter', sans-serif",
  '&.Mui-focused': {
    color: '#2563eb',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: '#ffffff',
  color: '#2d3748',
  fontFamily: "'Inter', sans-serif",
  '&:hover': {
    backgroundColor: '#f7fafc',
  },
  '&.Mui-selected': {
    backgroundColor: '#ebf8ff',
    color: '#2563eb',
  },
}));

const StatusChip = styled(Chip)(({ theme, isMainDomain }) => ({
  backgroundColor: isMainDomain ? '#059669' : '#dc2626',
  color: '#ffffff',
  fontWeight: 500,
  border: `1px solid ${isMainDomain ? '#059669' : '#dc2626'}`,
  fontFamily: "'Inter', sans-serif",
  '& .MuiChip-label': {
    fontSize: '0.8rem',
  },
}));

export default function QuickLLMSGenerator({ token, selectedReport }) {
  const [selectedDomain, setSelectedDomain] = useState('');
  const [expandedPreview, setExpandedPreview] = useState(false);

  const {
    loadingStates,
    previewData,
    error,
    clearError,
    downloadLLMS,
    previewLLMS
  } = useLLMSOperations(token);

  // Get domains from the selected report
  const domains = useMemo(() => {
    if (!selectedReport) return [];
    return [
      { domain: selectedReport.domain, isMain: true },
      ...(selectedReport.competitorDomains || []).map(domain => ({ domain, isMain: false }))
    ];
  }, [selectedReport]);

  // Reset selected domain when report changes
  useEffect(() => {
    if (domains.length > 0 && !selectedDomain) {
      setSelectedDomain(domains[0].domain);
    }
  }, [selectedReport, domains, selectedDomain]);

  // Don't render if no report is selected
  if (!selectedReport) {
    return (
      <StyledCard data-llms-generator>
        <CardContent>
          <CardTitle variant="h6" gutterBottom>
            Quick LLMS.txt Generator
          </CardTitle>
          <CardSubtitle variant="body2" color="textSecondary">
            Generate LLMS.txt files for domains from the selected report
          </CardSubtitle>
          
          <Typography variant="body1" sx={{ color: '#4a5568', fontFamily: "'Inter', sans-serif", textAlign: 'center' }}>
            Please select a report to use the Quick LLMS.txt Generator.
          </Typography>
        </CardContent>
      </StyledCard>
    );
  }

  const handleDomainChange = (event) => {
    setSelectedDomain(event.target.value);
  };

  const handleGenerate = async () => {
    if (!selectedDomain) return;
    await previewLLMS('domain', selectedDomain);
  };

  const handleDownload = async () => {
    if (!selectedDomain) return;
    await downloadLLMS('domain', selectedDomain);
  };

  const togglePreview = () => {
    setExpandedPreview(!expandedPreview);
  };

  return (
    <StyledCard data-llms-generator>
      <CardContent>
        <CardTitle variant="h6" gutterBottom>
          Quick LLMS.txt Generator
        </CardTitle>
        <CardSubtitle variant="body2" color="textSecondary">
          Generate LLMS.txt files for domains from the selected report
        </CardSubtitle>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: '250px' }}>
            <StyledInputLabel>Select Domain</StyledInputLabel>
            <StyledSelect
              value={selectedDomain}
              onChange={handleDomainChange}
              label="Select Domain"
            >
              <StyledMenuItem value="">
                <em>Select a domain...</em>
              </StyledMenuItem>
              {domains.map(({ domain, isMain }) => (
                <StyledMenuItem key={domain} value={domain}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{domain}</span>
                    <StatusChip 
                      label={isMain ? 'Your Domain' : 'Competitor'} 
                      isMainDomain={isMain}
                      size="small"
                    />
                  </Box>
                </StyledMenuItem>
              ))}
            </StyledSelect>
          </FormControl>
          
          <Typography variant="body2" sx={{ color: '#718096', fontFamily: "'Inter', sans-serif" }}>
            {domains.length} domain{domains.length !== 1 ? 's' : ''} available
          </Typography>
        </Box>

        <CardActions>
          <ActionButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleGenerate}
            disabled={!selectedDomain || loadingStates[`domain_${selectedDomain}`]}
          >
            {loadingStates[`domain_${selectedDomain}`] ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: '#ffffff' }} />
                Generating...
              </>
            ) : (
              'Generate Preview'
            )}
          </ActionButton>
          
          {previewData[`domain_${selectedDomain}`] && (
            <SecondaryButton
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={loadingStates[`domain_${selectedDomain}`]}
            >
              {loadingStates[`domain_${selectedDomain}`] ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1, color: '#2563eb' }} />
                  Downloading...
                </>
              ) : (
                'Download LLMS.txt'
              )}
            </SecondaryButton>
          )}
          
          {domains.length > 1 && (
            <SecondaryButton
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => {
                domains.forEach(({ domain }) => {
                  downloadLLMS('domain', domain);
                });
              }}
              disabled={Object.values(loadingStates).some(loading => loading)}
            >
              Generate All Domains
            </SecondaryButton>
          )}
        </CardActions>

        {/* Preview Section */}
        {previewData[`domain_${selectedDomain}`] && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ mr: 1, color: '#2563eb', fontFamily: "'Inter', sans-serif" }}>
                Preview:
              </Typography>
              <IconButton
                size="small"
                onClick={togglePreview}
                sx={{ color: '#2563eb' }}
              >
                {expandedPreview ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={expandedPreview}>
              <PreviewContainer>
                <PreviewContent>
                  {previewData[`domain_${selectedDomain}`].content}
                </PreviewContent>
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e2e8f0' }}>
                  <Typography variant="caption" sx={{ color: '#718096', fontFamily: "'Inter', sans-serif" }}>
                    Generated: {new Date(previewData[`domain_${selectedDomain}`].generatedAt).toLocaleString()} | 
                    Total URLs: {previewData[`domain_${selectedDomain}`].totalUrls} | 
                    URLs with Metadata: {previewData[`domain_${selectedDomain}`].urlsWithMetadata}
                  </Typography>
                </Box>
              </PreviewContainer>
            </Collapse>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
}
